/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  AmrUtilizationStatus,
  CardStatus,
  DiversionType,
  NotBillableType,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CardProps {
  id: string;
  status: CardStatus;
  cardNumber: string;
  dateContactCompleted: Date | null;
  dateCardClosed: Date | null;
  transferredToCardId: string | null;
  transferredFromCardId: string | null;
  linkedCardId: string | null;
  closeComment: string | null;
  reOpenComment: string | null;
  postTransportDebriefCompleted: boolean | null;
  isBillable: boolean | null;
  notBillableType: NotBillableType | null;
  notBillableComment: string | null;
  apiWebhookSubscriptionId: string | null;
  createdById: string | null;
  plannedCompletionTime: Date | null;
  reOpenReasonId: string | null;
  lastOperationId: string | null;
  diversionType: DiversionType | null;
  gmrUtilizationStatus: AmrUtilizationStatus | null;
  gmrCADNumber: string | null;
  isLocked: boolean | null;
  crewWillTransportWithReplacementGroundUnit: boolean | null;
  operationId: string | null;
  requestId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CardRecord extends Record<CardProps>({
  id: null as any,
  status: null as any,
  cardNumber: null as any,
  dateContactCompleted: null as any,
  dateCardClosed: null as any,
  transferredToCardId: null as any,
  transferredFromCardId: null as any,
  linkedCardId: null as any,
  closeComment: null as any,
  reOpenComment: null as any,
  postTransportDebriefCompleted: null as any,
  isBillable: null as any,
  notBillableType: null as any,
  notBillableComment: null as any,
  apiWebhookSubscriptionId: null as any,
  createdById: null as any,
  plannedCompletionTime: null as any,
  reOpenReasonId: null as any,
  lastOperationId: null as any,
  diversionType: null as any,
  gmrUtilizationStatus: null as any,
  gmrCADNumber: null as any,
  isLocked: null as any,
  crewWillTransportWithReplacementGroundUnit: null as any,
  operationId: null as any,
  requestId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    status: CardStatus;
    cardNumber: string;
    dateContactCompleted: string | number | Date | null;
    dateCardClosed: string | number | Date | null;
    transferredToCardId: string | null;
    transferredFromCardId: string | null;
    linkedCardId: string | null;
    closeComment: string | null;
    reOpenComment: string | null;
    postTransportDebriefCompleted: boolean | null;
    isBillable: boolean | null;
    notBillableType: NotBillableType | null;
    notBillableComment: string | null;
    apiWebhookSubscriptionId: string | null;
    createdById: string | null;
    plannedCompletionTime: string | number | Date | null;
    reOpenReasonId: string | null;
    lastOperationId: string | null;
    diversionType: DiversionType | null;
    gmrUtilizationStatus: AmrUtilizationStatus | null;
    gmrCADNumber: string | null;
    isLocked: boolean | null;
    crewWillTransportWithReplacementGroundUnit: boolean | null;
    operationId: string | null;
    requestId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CardRecord {
    if (json == null) {
      throw new Error('CardRecord.fromJS: parameter cannot be null');
    }
    return new CardRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CardJson.id',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', CardStatus.AwaitingAssignment),
        'CardJson.status',
      ),
      cardNumber: getFromOrThrow(json, (j) => j.cardNumber, 'CardJson.cardNumber'),
      dateContactCompleted:
        json.dateContactCompleted != null ? new Date(json.dateContactCompleted) : null,
      dateCardClosed: json.dateCardClosed != null ? new Date(json.dateCardClosed) : null,
      transferredToCardId: json.transferredToCardId,
      transferredFromCardId: json.transferredFromCardId,
      linkedCardId: json.linkedCardId,
      closeComment: json.closeComment,
      reOpenComment: json.reOpenComment,
      postTransportDebriefCompleted: json.postTransportDebriefCompleted,
      isBillable: json.isBillable,
      notBillableType: json.notBillableType,
      notBillableComment: json.notBillableComment,
      apiWebhookSubscriptionId: json.apiWebhookSubscriptionId,
      createdById: json.createdById,
      plannedCompletionTime:
        json.plannedCompletionTime != null ? new Date(json.plannedCompletionTime) : null,
      reOpenReasonId: json.reOpenReasonId,
      lastOperationId: json.lastOperationId,
      diversionType: json.diversionType,
      gmrUtilizationStatus: json.gmrUtilizationStatus,
      gmrCADNumber: json.gmrCADNumber,
      isLocked: json.isLocked,
      crewWillTransportWithReplacementGroundUnit: json.crewWillTransportWithReplacementGroundUnit,
      operationId: json.operationId,
      requestId: json.requestId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'CardJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    status: CardStatus;
    cardNumber: string;
    dateContactCompleted: string | number | Date | null;
    dateCardClosed: string | number | Date | null;
    transferredToCardId: string | null;
    transferredFromCardId: string | null;
    linkedCardId: string | null;
    closeComment: string | null;
    reOpenComment: string | null;
    postTransportDebriefCompleted: boolean | null;
    isBillable: boolean | null;
    notBillableType: NotBillableType | null;
    notBillableComment: string | null;
    apiWebhookSubscriptionId: string | null;
    createdById: string | null;
    plannedCompletionTime: string | number | Date | null;
    reOpenReasonId: string | null;
    lastOperationId: string | null;
    diversionType: DiversionType | null;
    gmrUtilizationStatus: AmrUtilizationStatus | null;
    gmrCADNumber: string | null;
    isLocked: boolean | null;
    crewWillTransportWithReplacementGroundUnit: boolean | null;
    operationId: string | null;
    requestId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CardRecord, Error[]] {
    if (json == null) {
      throw new Error('CardRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CardRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CardJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', CardStatus.AwaitingAssignment),
            'CardJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardNumber: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.cardNumber, 'CardJson.cardNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateContactCompleted: tryOrDefault(
        () => (json.dateContactCompleted != null ? new Date(json.dateContactCompleted) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateCardClosed: tryOrDefault(
        () => (json.dateCardClosed != null ? new Date(json.dateCardClosed) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transferredToCardId: tryOrDefault(
        () => json.transferredToCardId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transferredFromCardId: tryOrDefault(
        () => json.transferredFromCardId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      linkedCardId: tryOrDefault(
        () => json.linkedCardId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      closeComment: tryOrDefault(
        () => json.closeComment,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reOpenComment: tryOrDefault(
        () => json.reOpenComment,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      postTransportDebriefCompleted: tryOrDefault(
        () => json.postTransportDebriefCompleted,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isBillable: tryOrDefault(
        () => json.isBillable,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notBillableType: tryOrDefault(
        () => json.notBillableType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notBillableComment: tryOrDefault(
        () => json.notBillableComment,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      apiWebhookSubscriptionId: tryOrDefault(
        () => json.apiWebhookSubscriptionId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      createdById: tryOrDefault(
        () => json.createdById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      plannedCompletionTime: tryOrDefault(
        () => (json.plannedCompletionTime != null ? new Date(json.plannedCompletionTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reOpenReasonId: tryOrDefault(
        () => json.reOpenReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastOperationId: tryOrDefault(
        () => json.lastOperationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      diversionType: tryOrDefault(
        () => json.diversionType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gmrUtilizationStatus: tryOrDefault(
        () => json.gmrUtilizationStatus,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gmrCADNumber: tryOrDefault(
        () => json.gmrCADNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isLocked: tryOrDefault(
        () => json.isLocked,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      crewWillTransportWithReplacementGroundUnit: tryOrDefault(
        () => json.crewWillTransportWithReplacementGroundUnit,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationId: tryOrDefault(
        () => json.operationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestId: tryOrDefault(
        () => json.requestId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'CardJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CardProps) {
    super(props);
    if (props == null) {
      throw new Error('CardRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CardViewModelBase implements CardProps {
  protected readonly _props: CardProps;
  private _json?: CardProps;

  get id(): string {
    return this._props.id;
  }
  get status(): CardStatus {
    return this._props.status;
  }
  get cardNumber(): string {
    return this._props.cardNumber;
  }
  get dateContactCompleted(): Date | null {
    return this._props.dateContactCompleted;
  }
  get dateCardClosed(): Date | null {
    return this._props.dateCardClosed;
  }
  get transferredToCardId(): string | null {
    return this._props.transferredToCardId;
  }
  get transferredFromCardId(): string | null {
    return this._props.transferredFromCardId;
  }
  get linkedCardId(): string | null {
    return this._props.linkedCardId;
  }
  get closeComment(): string | null {
    return this._props.closeComment;
  }
  get reOpenComment(): string | null {
    return this._props.reOpenComment;
  }
  get postTransportDebriefCompleted(): boolean | null {
    return this._props.postTransportDebriefCompleted;
  }
  get isBillable(): boolean | null {
    return this._props.isBillable;
  }
  get notBillableType(): NotBillableType | null {
    return this._props.notBillableType;
  }
  get notBillableComment(): string | null {
    return this._props.notBillableComment;
  }
  get apiWebhookSubscriptionId(): string | null {
    return this._props.apiWebhookSubscriptionId;
  }
  get createdById(): string | null {
    return this._props.createdById;
  }
  get plannedCompletionTime(): Date | null {
    return this._props.plannedCompletionTime;
  }
  get reOpenReasonId(): string | null {
    return this._props.reOpenReasonId;
  }
  get lastOperationId(): string | null {
    return this._props.lastOperationId;
  }
  get diversionType(): DiversionType | null {
    return this._props.diversionType;
  }
  get gmrUtilizationStatus(): AmrUtilizationStatus | null {
    return this._props.gmrUtilizationStatus;
  }
  get gmrCADNumber(): string | null {
    return this._props.gmrCADNumber;
  }
  get isLocked(): boolean | null {
    return this._props.isLocked;
  }
  get crewWillTransportWithReplacementGroundUnit(): boolean | null {
    return this._props.crewWillTransportWithReplacementGroundUnit;
  }
  get operationId(): string | null {
    return this._props.operationId;
  }
  get requestId(): string | null {
    return this._props.requestId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAwaitingAssignment() {
    return this._props.status === CardStatus.AwaitingAssignment;
  }
  public get isAwaitingLaunch() {
    return this._props.status === CardStatus.AwaitingLaunch;
  }
  public get isClosed() {
    return this._props.status === CardStatus.Closed;
  }
  public get isCompleted() {
    return this._props.status === CardStatus.Completed;
  }
  public get isUnderway() {
    return this._props.status === CardStatus.Underway;
  }
  public get isMechanicalAbort() {
    return this._props.notBillableType === NotBillableType.MechanicalAbort;
  }
  public get isOther() {
    return this._props.notBillableType === NotBillableType.Other;
  }
  public get isPatientCondition() {
    return this._props.notBillableType === NotBillableType.PatientCondition;
  }
  public get isPatientExpiredOrQL() {
    return this._props.notBillableType === NotBillableType.PatientExpiredOrQL;
  }
  public get isPatientWentByGround() {
    return this._props.notBillableType === NotBillableType.PatientWentByGround;
  }
  public get isWeatherAbort() {
    return this._props.notBillableType === NotBillableType.WeatherAbort;
  }
  public get isDiversion() {
    return this._props.diversionType === DiversionType.Diversion;
  }
  public get isFollowOn() {
    return this._props.diversionType === DiversionType.FollowOn;
  }
  public get isNoResourcesAvailable() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.NoResourcesAvailable;
  }
  public get isNoServiceAvailable() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.NoServiceAvailable;
  }
  public get isNotConsidered() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.NotConsidered;
  }
  public get isUnsatisfactoryEta() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.UnsatisfactoryEta;
  }
  public get isUnspecified() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.Unspecified;
  }
  public get isUtilized() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.Utilized;
  }

  public isAnyStatus(...cardStatus: CardStatus[]) {
    return cardStatus.includes(this._props.status);
  }
  public isAnyNotBillableType(...notBillableType: NotBillableType[]) {
    return (
      this._props.notBillableType != null && notBillableType.includes(this._props.notBillableType)
    );
  }
  public isAnyDiversionType(...diversionType: DiversionType[]) {
    return this._props.diversionType != null && diversionType.includes(this._props.diversionType);
  }
  public isAnyGmrUtilizationStatus(...amrUtilizationStatus: AmrUtilizationStatus[]) {
    return (
      this._props.gmrUtilizationStatus != null &&
      amrUtilizationStatus.includes(this._props.gmrUtilizationStatus)
    );
  }

  constructor(props: CardProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        status: this._props.status,
        cardNumber: this._props.cardNumber,
        dateContactCompleted: this._props.dateContactCompleted,
        dateCardClosed: this._props.dateCardClosed,
        transferredToCardId: this._props.transferredToCardId,
        transferredFromCardId: this._props.transferredFromCardId,
        linkedCardId: this._props.linkedCardId,
        closeComment: this._props.closeComment,
        reOpenComment: this._props.reOpenComment,
        postTransportDebriefCompleted: this._props.postTransportDebriefCompleted,
        isBillable: this._props.isBillable,
        notBillableType: this._props.notBillableType,
        notBillableComment: this._props.notBillableComment,
        apiWebhookSubscriptionId: this._props.apiWebhookSubscriptionId,
        createdById: this._props.createdById,
        plannedCompletionTime: this._props.plannedCompletionTime,
        reOpenReasonId: this._props.reOpenReasonId,
        lastOperationId: this._props.lastOperationId,
        diversionType: this._props.diversionType,
        gmrUtilizationStatus: this._props.gmrUtilizationStatus,
        gmrCADNumber: this._props.gmrCADNumber,
        isLocked: this._props.isLocked,
        crewWillTransportWithReplacementGroundUnit:
          this._props.crewWillTransportWithReplacementGroundUnit,
        operationId: this._props.operationId,
        requestId: this._props.requestId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCardEntities = (state: AppState) => state.entityStores.card.entities;

export const selectCardById = (id: string) => (state: AppState) =>
  state.entityStores.card.entities.get(id);

export const selectCardMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.card.meta.get(id);

export const selectCardAndMetadataById = (id: string) =>
  createSelector(selectCardById(id), selectCardMetadataById(id), (card, meta) => ({ card, meta }));

export function selectCardEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCardEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CardRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCardEntitiesByStatus(status: CardStatus) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectCardEntitiesByCardNumber(cardNumber: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.cardNumber === cardNumber).valueSeq(),
  );
}

export function selectCardEntitiesByDateContactCompleted(dateContactCompleted: Date) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.dateContactCompleted === dateContactCompleted).valueSeq(),
  );
}

export function selectCardEntitiesByDateCardClosed(dateCardClosed: Date) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.dateCardClosed === dateCardClosed).valueSeq(),
  );
}

export function selectCardEntitiesByTransferredToCardId(transferredToCardId: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.transferredToCardId === transferredToCardId).valueSeq(),
  );
}

export function selectCardEntitiesByTransferredFromCardId(transferredFromCardId: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.transferredFromCardId === transferredFromCardId).valueSeq(),
  );
}

export function selectCardEntitiesByLinkedCardId(linkedCardId: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.linkedCardId === linkedCardId).valueSeq(),
  );
}

export function selectCardEntitiesByCloseComment(closeComment: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.closeComment === closeComment).valueSeq(),
  );
}

export function selectCardEntitiesByReOpenComment(reOpenComment: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.reOpenComment === reOpenComment).valueSeq(),
  );
}

export function selectCardEntitiesByPostTransportDebriefCompleted(
  postTransportDebriefCompleted: boolean,
) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities
      .filter((e) => e.postTransportDebriefCompleted === postTransportDebriefCompleted)
      .valueSeq(),
  );
}

export function selectCardEntitiesByIsBillable(isBillable: boolean) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.isBillable === isBillable).valueSeq(),
  );
}

export function selectCardEntitiesByNotBillableType(notBillableType: NotBillableType) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.notBillableType === notBillableType).valueSeq(),
  );
}

export function selectCardEntitiesByNotBillableComment(notBillableComment: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.notBillableComment === notBillableComment).valueSeq(),
  );
}

export function selectCardEntitiesByApiWebhookSubscriptionId(apiWebhookSubscriptionId: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.apiWebhookSubscriptionId === apiWebhookSubscriptionId).valueSeq(),
  );
}

export function selectCardEntitiesByCreatedById(createdById: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.createdById === createdById).valueSeq(),
  );
}

export function selectCardEntitiesByPlannedCompletionTime(plannedCompletionTime: Date) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.plannedCompletionTime === plannedCompletionTime).valueSeq(),
  );
}

export function selectCardEntitiesByReOpenReasonId(reOpenReasonId: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.reOpenReasonId === reOpenReasonId).valueSeq(),
  );
}

export function selectCardEntitiesByLastOperationId(lastOperationId: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.lastOperationId === lastOperationId).valueSeq(),
  );
}

export function selectCardEntitiesByDiversionType(diversionType: DiversionType) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.diversionType === diversionType).valueSeq(),
  );
}

export function selectCardEntitiesByGmrUtilizationStatus(
  gmrUtilizationStatus: AmrUtilizationStatus,
) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.gmrUtilizationStatus === gmrUtilizationStatus).valueSeq(),
  );
}

export function selectCardEntitiesByGmrCADNumber(gmrCADNumber: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.gmrCADNumber === gmrCADNumber).valueSeq(),
  );
}

export function selectCardEntitiesByIsLocked(isLocked: boolean) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.isLocked === isLocked).valueSeq(),
  );
}

export function selectCardEntitiesByCrewWillTransportWithReplacementGroundUnit(
  crewWillTransportWithReplacementGroundUnit: boolean,
) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities
      .filter(
        (e) =>
          e.crewWillTransportWithReplacementGroundUnit ===
          crewWillTransportWithReplacementGroundUnit,
      )
      .valueSeq(),
  );
}

export function selectCardEntitiesByOperationId(operationId: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.operationId === operationId).valueSeq(),
  );
}

export function selectCardEntitiesByRequestId(requestId: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.requestId === requestId).valueSeq(),
  );
}

export function selectCardEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllCardEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
